import { useCallback, useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import type { Readiness } from "@itspip/shared";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  useCoachObjective,
  useCoachKeyResults,
  type CoachError,
  type KrTypeInput,
  type ObjectiveLevel,
  type ObjectiveType,
} from "@/hooks/use-coach";
import { sanitizeCoachInput } from "@/lib/coach-input";
import { CoachFeedbackCard } from "./coach-feedback-card";

interface ObjectiveCoachPanelProps {
  statement: string;
  level: ObjectiveLevel;
  type: ObjectiveType;
}

export function ObjectiveCoachPanel({
  statement,
  level,
  type,
}: ObjectiveCoachPanelProps) {
  const [open, setOpen] = useState(false);
  const { feedback, isStreaming, error, signals, analyze, reset } =
    useCoachObjective();

  const cleaned = sanitize(statement);
  const canCoach = !isStreaming && cleaned !== null;

  const requestCoaching = useCallback(async () => {
    if (!cleaned) return;
    await analyze({
      objective_statement: cleaned,
      level,
      type,
    });
  }, [analyze, cleaned, level, type]);

  const hasContent = feedback.length > 0 || error !== null || isStreaming;

  return (
    <CoachPanelShell
      open={open}
      onToggle={() => setOpen((v) => !v)}
      canCoach={canCoach}
      isStreaming={isStreaming}
      onCoach={() => {
        setOpen(true);
        void requestCoaching();
      }}
      hasContent={hasContent}
      feedback={feedback}
      error={error}
      readiness={signals.readiness}
      onRetry={() => {
        reset();
        void requestCoaching();
      }}
    />
  );
}

interface KrCoachPanelProps {
  objectiveStatement: string;
  krTitle: string;
  krType: KrTypeInput;
  krTargetValue: number | null;
}

export function KrCoachPanel({
  objectiveStatement,
  krTitle,
  krType,
  krTargetValue,
}: KrCoachPanelProps) {
  const [open, setOpen] = useState(false);
  const { feedback, isStreaming, error, signals, analyze, reset } =
    useCoachKeyResults();

  const cleanedObjective = sanitize(objectiveStatement);
  const cleanedTitle = sanitize(krTitle);
  const canCoach =
    !isStreaming && cleanedObjective !== null && cleanedTitle !== null;

  const requestCoaching = useCallback(async () => {
    if (!cleanedObjective || !cleanedTitle) return;
    await analyze({
      objective_statement: cleanedObjective,
      key_results: [
        {
          title: cleanedTitle,
          type: krType,
          target_value: krTargetValue,
        },
      ],
      mode: "individual",
    });
  }, [analyze, cleanedObjective, cleanedTitle, krType, krTargetValue]);

  const hasContent = feedback.length > 0 || error !== null || isStreaming;

  return (
    <CoachPanelShell
      open={open}
      onToggle={() => setOpen((v) => !v)}
      canCoach={canCoach}
      isStreaming={isStreaming}
      onCoach={() => {
        setOpen(true);
        void requestCoaching();
      }}
      hasContent={hasContent}
      feedback={feedback}
      error={error}
      readiness={signals.readiness}
      onRetry={() => {
        reset();
        void requestCoaching();
      }}
    />
  );
}

interface ShellProps {
  open: boolean;
  onToggle: () => void;
  canCoach: boolean;
  isStreaming: boolean;
  onCoach: () => void;
  hasContent: boolean;
  feedback: string;
  error: CoachError | null;
  readiness: Readiness | null;
  onRetry: () => void;
}

function CoachPanelShell({
  open,
  onToggle,
  canCoach,
  isStreaming,
  onCoach,
  hasContent,
  feedback,
  error,
  readiness,
  onRetry,
}: ShellProps) {
  return (
    <Card className="bg-[hsl(var(--muted))]">
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-heading text-sm font-semibold">OKR Coaching</p>
              <ReadinessBadge readiness={readiness} />
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Get AI feedback on this draft before transitioning out of Draft status.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onCoach}
              disabled={!canCoach}
            >
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              {isStreaming ? "Coaching..." : "Get Coaching Feedback"}
            </Button>
            {hasContent && (
              <Button variant="ghost" size="sm" onClick={onToggle}>
                {open ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {open && hasContent && (
          <CoachFeedbackCard
            feedback={feedback}
            roundNumber={1}
            isStreaming={isStreaming}
            error={error}
            onRetry={error && error.kind !== "aborted" ? onRetry : undefined}
          />
        )}
      </CardContent>
    </Card>
  );
}

function ReadinessBadge({ readiness }: { readiness: Readiness | null }) {
  if (readiness !== "ready" && readiness !== "ready_with_caveats") return null;
  const ready = readiness === "ready";
  return (
    <span
      data-testid="coach-readiness-badge"
      className={
        ready
          ? "inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800"
          : "inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
      }
    >
      <CheckCircle2 className="h-3 w-3" />
      {ready ? "Ready" : "Ready with caveats"}
    </span>
  );
}

const sanitize = (raw: string) => sanitizeCoachInput(raw);
